import React from "react";
import AppLayout from "../../lib/components/Layout/app-layout";
import Header from "../../lib/components/Header";
import scrollHandler from "../../utils/scrollHandler";
import { useNavigate } from "react-router-dom";
import pathnames from "../../constants/pathnames";
import AppTextField from "../../lib/components/AppTextField";
import { InputAdornment, IconButton } from "@mui/material";
import { Search, ArrowBack } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import walletService from "../../service/walletService";
import DataTable from "../../lib/components/DataTable";
import useDebounce from "../../hooks/useDebounce";
import { toast } from "react-toastify";
import AppExportButton from "../../lib/components/AppExportButton";
import { moneyFormat } from "../../utils/moneyFormat";
import moment from "moment";

const FundingHistory = () => {
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(window.location.search);
  const page = searchParams.get("page") || "1";
  const limit = searchParams.get("limit") || "20";
  const keyword = searchParams.get("keyword") || "";

  const searchTerm = useDebounce(keyword);
  const query = { page, limit, keyword: searchTerm };

  const fundingsQuery = useQuery({
    retry: false,
    queryKey: ["fundings", ...Object.values(query)],
    queryFn: () => walletService.getFundings(query),
  });

  const fundingsData = fundingsQuery?.data?.data;
  const rawDocs = fundingsData?.docs || fundingsData?.data || [];

  // Filter locally by keyword if needed (for extra responsiveness)
  const filteredDocs = React.useMemo(() => {
    if (!searchTerm) return rawDocs;
    const term = searchTerm.toLowerCase();
    return rawDocs.filter((doc) => {
      const matchDate = doc.date && moment(doc.date).format("Do MMM, YYYY").toLowerCase().includes(term);
      const matchRef = (doc.reference || doc.transactionId) && String(doc.reference || doc.transactionId).toLowerCase().includes(term);
      const matchAmt = doc.amount && String(doc.amount).includes(term);
      return matchDate || matchRef || matchAmt;
    });
  }, [rawDocs, searchTerm]);

  const [isExporting, setIsExporting] = React.useState(false);

  const handleExport = async () => {
    setIsExporting(true);
    try {
      // CSV generation and download
      const headers = ["DATE,TRANSACTION ID,AMOUNT FUNDED\n"];
      const rows = filteredDocs.map(
        (doc) =>
          `"${moment(doc.date || doc.createdAt).format("YYYY-MM-DD")}",` +
          `"${doc.reference || doc.transactionId || "N/A"}",` +
          `"${doc.amount || 0}"\n`
      );
      const blob = new Blob([...headers, ...rows], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute("download", `Funding_History_${moment().format("YYYY-MM-DD")}.csv`);
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("CSV exported successfully!");
    } catch (e) {
      toast.error("Failed to export Funding History CSV");
    } finally {
      setIsExporting(false);
    }
  };

  const columns = [
    {
      key: "date",
      header: "Date",
      render: (_, row) => moment(row.date || row.createdAt).format("Do MMM, YYYY"),
      filter: "date",
    },
    {
      key: "reference",
      header: "Transaction ID",
      render: (val, row) => {
        const txnId = val || row.transactionId || row.reference || row._id || row.id;
        const displayId = txnId ? (txnId.length > 10 ? `#TXN-${txnId.slice(-8).toUpperCase()}` : txnId) : "N/A";
        return (
          <span className="fw-medium">{displayId}</span>
        );
      },
      filter: "text",
    },
    {
      key: "amount",
      header: "Amount Funded",
      type: "currency",
      render: (value) => (
        <span className="fw-medium">{moneyFormat(value, "NGN")}</span>
      ),
    },
  ];

  return (
    <AppLayout>
      <Header
        title="Funding History"
        description="See manual funding logs for the Paystack wallet replenishment"
      />

      <div className="p-3 bg-light" style={{ minHeight: "100%" }}>
        <IconButton
          onClick={() => navigate(pathnames.PAYOUTS)}
          className="bg-secondary-subtle mb-3"
        >
          <ArrowBack />
        </IconButton>
        <div className="row mb-3 align-items-center justify-content-between">
          <div className="col-12 col-md-4 mb-3">
            <AppTextField
              placeholder="Search by date, transaction ID or amount..."
              value={keyword}
              onChange={(e) => handleChange(e, "keyword")}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <Search className="text-secondary" />
                  </InputAdornment>
                ),
              }}
            />
          </div>
          <div className="col-12 col-md-8 mb-3 d-flex justify-content-md-end">
            <AppExportButton onClick={handleExport} isLoading={isExporting} />
          </div>
        </div>

        <DataTable
          data={filteredDocs}
          columns={columns}
          selectable={false}
          isLoading={fundingsQuery.isLoading}
          getRowId={(row) => row._id || row.id || row.reference}
          page={Number(page)}
          pageSize={Number(limit)}
          totalPages={fundingsData?.totalPages || 1}
          onPageChange={(value) => handleChange(value, "page")}
          onPageSizeChange={(value) =>
            handleChange({ target: { value } }, "limit")
          }
        />
      </div>
    </AppLayout>
  );

  function handleChange(e, name = "") {
    const value = name === "page" ? e : e.target.value;
    searchParams.set(name, value);
    if (name === "keyword") {
      searchParams.set("page", "1");
    }
    navigate(
      {
        pathname: window.location.pathname,
        search: searchParams.toString(),
      },
      { replace: true },
      { scrollY: 0 }
    );
    if (name === "page") scrollHandler();
  }
};

export default FundingHistory;
